import { useQuery, useQueryClient } from "@tanstack/react-query";
import moment from "moment";
import { custom, z } from "zod";
import { Decimal } from "decimal.js";
import {
    fetchApiGet,
    fetchApiPut,
    fetchApiPost,
    fetchApiDelete,
} from "../../utils";
import { PossibleRole } from "@/lib/permissions/stytch_original";
import { Commodities } from "../../commodity";
import { transformToDate, transformToDecimal } from "@/lib/api/common";
import { BaseDbObject } from "@/lib/models/db";

export const CashSalesFiltersValidation = z.object({
    ids: z.array(z.string().uuid()).optional(),
    is_open: z.coerce.boolean().optional(),
    is_enabled: z.coerce.boolean().default(true),
    customer_id: z.string().uuid().optional(),
    trader_id: z.string().uuid().optional(),
    trader_ids: z.array(z.string().uuid()).optional(),
    customer_ids: z.array(z.string().uuid()).optional(),
    location_id: z.string().uuid().optional(),
    market_zone_id: z.string().uuid().optional(),
    commodity_types: z.array(z.string()).optional(),
    commodity: z.string().optional(),
    crop_year: z.coerce.number().optional(),
    contract_number: z.string().optional(),
    contract_release: z.string().optional(),
    option_month: z.string().optional().nullable(),
    contract_type: z.string().optional(),
    pr_status: z.string().optional(),
    status: z.string().optional(),
    futures: z.string().optional(),
    basis: z.string().optional(),
    flag_status: z.array(z.string()).optional(),
    flag_rule: z.array(z.string()).optional(),
    range_start: z.string().optional(),
    range_end: z.string().optional(),
    has_a_flag_status: z.coerce.boolean().optional(),
    surface_if_flagged_compound_id_appears: z.boolean().optional(),
});

export const UpdateCashSaleRowsValidation = z.object({
  id: z.string().uuid(),

  crop_year: z.number(),
  option_month: z.string().optional().nullable(),

  status: z.string().optional().nullable(),
  pr_status: z.string().optional().nullable(),

  customer_id: z.string().uuid().optional().nullable(),
  market_zone_id: z.string().uuid().optional().nullable(),
  commodity: z.string().optional().nullable(),

  // THESE Dates before being sent to the backend must be in the format "YYYY/MM/DD"
  start_date: z.string().optional(),
  end_date: z.string().optional(),
  add_date: z.string().optional().nullable(),


  // Note these are all optional Decimal.js, but converted to strings
  futures: z.string().optional().nullable(),
  basis: z.string().optional().nullable(),
  quantity: z.string().optional().nullable(),
  scheduled_quantity: z.string().optional().nullable(),
  balance: z.string().optional().nullable(),
  price: z.string().optional().nullable(),
  loads: z.string().optional().nullable(),
});

export enum CashSaleStatus {
    CancelledNotPrinted = 'Cancelled,Not Printed',
    ClosedNotPrinted = 'Closed,Not Printed',
    ClosedPrinted = 'Closed,Printed',
    Closed = 'Closed',
    OpenNotPrinted = 'Open,Not Printed',
    OpenPrinted = 'Open,Printed',
    ManualOpen = 'open',
}

export enum PrStatus {
    HTA = 'HTA',
    PP = 'PP',
    PR = 'PR',
    TBD = 'TBD',
    UNP = 'UNP',
}

export enum ActionCashSales {
    Delete = "delete",
    Create = "create",
    Update = "update"
}

export const ActionOnCashSaleRowsValidation = z.object({
    action: z.nativeEnum(ActionCashSales),
    data: z.object({
        ids: z.array(z.string().uuid()),
        update_info: z.object({
            base_id: z.string().uuid()
        }).optional()
    })
});


export enum RowFlagStatus {
    SurfaceAndIncludeInStats = "surface_and_include_in_stats",
    SurfaceAndExcludeFromStats = "surface_and_exclude_from_stats",
    DoNotSurfaceOrShowInStats = "do_not_surface_or_show_in_stats",
}

export enum RowFlagRule {
    // Valid rows that we couldnt figure out how to map to existing rows
    KnownButMultipleRows = "known_but_multiple_rows",
    // Invalid rule filtering checks
    HtaAllBasisSet = "hta_all_basis_set",
    HtaSomeBasisSet = "hta_some_basis_set",
    ScheduledQuantityAndBasis = "scheduled_quantity_and_basis",
    Cancelled = 'cancelled',
    CatchAll = "catch_all",
}

export interface RiskCashSale extends BaseDbObject {
    is_open: boolean;
  is_automated?: boolean;
    contract_type: string;
    contract_number: string;
    contract_release: string;

    status: string;
    compound_id: string;
    comparable_id: string;
    pr_status: string;

    location_id: string;
    market_zone_id: string;
    trader_id: string;
    customer_id: string;

    destination?: string;
    washed_gain_loss?: string;

    start_date: Date;
    end_date: Date;
    add_date?: Date;

    commodity: string;
    commodity_type: string;
    option_month?: string;
    crop_year: number;

    ct_uom?: string;
    pr_uom?: string;
    uom?: string;

    scheduled_quantity?: Decimal;
    quantity: Decimal;
    balance?: Decimal;
    futures?: Decimal;
    basis?: Decimal;
    price?: Decimal;
    loads?: string;

    impliedFutures?: Decimal | null;
    impliedCost?: Decimal | null;

    is_enabled: boolean;
    is_manually_inserted: boolean;
    deleted_at?: Date;

    flag_status: RowFlagStatus;
    flag_rule: RowFlagRule;

    report_customer_id?: string;
    report_market_zone_id?: string;
    report_location_id?: string;
    report_trader_id?: string;

    appears_in_confirmed?: boolean;

    note?: string;

    Customer?: { long_name: string }
    MarketZone?: { dtn_market_zone: string }

    // OTC contrac type if found
    detailed_contract_type?: string;
}

export interface ListCashSalesResult {
    data: RiskCashSale[]
}

export function useListRiskCashSales({ filters }: { filters?: z.infer<typeof CashSalesFiltersValidation> }) {
    const queryParams = new URLSearchParams();

    if (filters) {
        if (filters.status) {
            queryParams.set("status", filters.status);
        }

        if (filters.flag_status?.length! > 0) {
            queryParams.set("flag_status", JSON.stringify((filters.flag_status)));
        }

        if (filters.flag_rule?.length! > 0) {
            queryParams.set("flag_rule", JSON.stringify((filters.flag_rule)));
        }

        if (filters.commodity_types?.length! > 0) {
            queryParams.set("commodity_types", JSON.stringify((filters.commodity_types)));
        }

        if (filters.customer_ids?.length! > 0) {
            queryParams.set("customer_ids", JSON.stringify((filters.customer_ids)));
        }

        if (filters.trader_ids?.length! > 0) {
            queryParams.set("trader_ids", JSON.stringify((filters.trader_ids)));
        }

        if (filters.contract_number) {
            queryParams.set("contract_number", filters.contract_number);
        }
        if (filters.contract_release) {
            queryParams.set("contract_release", filters.contract_release);
        }
        if (filters.commodity) {
            queryParams.set("commodity", filters.commodity);
        }
        if (filters.location_id) {
            queryParams.set("location_id", filters.location_id);
        }
        if (filters.option_month) {
            queryParams.set("option_month", filters.option_month);
        }
        if (filters.is_enabled !== undefined) {
            queryParams.set("is_enabled", filters.is_enabled.toString());
        }
        if (filters.has_a_flag_status !== undefined) {
            queryParams.set("has_a_flag_status", filters.has_a_flag_status.toString());
        }
        if (filters.surface_if_flagged_compound_id_appears !== undefined) {
            queryParams.set("surface_if_flagged_compound_id_appears", filters.surface_if_flagged_compound_id_appears.toString());
        }
        if (filters.contract_type) {
            queryParams.set("contract_type", filters.contract_type);
        }
        if (filters.customer_id) {
            queryParams.set("customer_id", filters.customer_id);
        }
        if (filters.market_zone_id) {
            queryParams.set("market_zone_id", filters.market_zone_id);
        }
        if (filters.crop_year != null) {
            queryParams.set("crop_year", filters.crop_year?.toString());
        }
        if (filters.trader_id) {
            queryParams.set("trader_id", filters.trader_id);
        }
        if (filters.range_start) {
            queryParams.set("range_start", moment(filters.range_start).format('YYYY-MM-DD'));
        }
        if (filters.range_end) {
            queryParams.set("range_end", moment(filters.range_end).format('YYYY-MM-DD'));
        }
    }

    return useQuery({
        queryKey: ["risk", "cash_sales", "top_level", queryParams.toString()],
        queryFn: () =>
            fetchApiGet<ListCashSalesResult>("risk/cashSale", queryParams),
        retry: 2,
        keepPreviousData: true,
    });
}


export function useActionCashSaleRows() {
    const queryClient = useQueryClient();
    const queryParams = new URLSearchParams();

    return async (data: z.infer<typeof ActionOnCashSaleRowsValidation>) => {
        const result = await fetchApiPut<{}>(
            `risk/cashSale`,
            data,
        );

        if (result.success) {
            queryClient.invalidateQueries({ queryKey: ["risk", "cash_sales"] });
        }

        return result;
    };
}

export function useUpdateCashSaleRows() {
    const queryClient = useQueryClient();
    const queryParams = new URLSearchParams();

    return async (data: z.infer<typeof UpdateCashSaleRowsValidation>) => {
        const result = await fetchApiPut<{}>(
            `risk/cashSale/${data.id}`,
            data,
        );

        if (result.success) {
            queryClient.invalidateQueries({ queryKey: ["risk", "cash_sales"] });
        }

        return result;
    };
}
